import { ChevronRightIcon } from '@heroicons/react/24/outline';

import { NeedsApprovalShift } from '../api/types';
import { cardActivation } from '../util/cardActivation';
import { formatShiftDateTime } from '../util/dateFormat';

// One needs-approval row — one SHIFT with a pending decision: Date & time ·
// Client · Skill · Location · the Hired fraction, and a chevron that opens the
// Shift Detail panel for that occurrence.
//
// The row used to be per job, with three heuristic fraction sub-states (Hired /
// For Hired / For approval). Product settled it: rows are per shift and the
// label is always "Hired", hired workers on this occurrence over its slots
// (GRAVY-10301).
//
// The chevron used to be a <Link> to /orders/:order_id/jobs/:job_id, which
// dropped the admin out of Dashboard V2 onto the legacy job page and lost the
// queue entirely — GRAVY-10223 requires it to expand into Shift Detail
// (GRAVY-10296). It is a <button> now: it performs an in-page action, so it was
// never a link.

type Props = {
  shift: NeedsApprovalShift;
  /** Opens the Shift Detail panel. Omitted only in isolated component tests. */
  onSelect?: (shiftId: number) => void;
};

const NeedsApprovalRow = ({ shift, onSelect }: Props) => {
  const tz = shift.timezone;
  // Always present now that rows are shift-based.
  const shiftId = shift.shift_id;

  const col = (label: string, value: string, modifier: string) => (
    <div className={`dashv2-nscol dashv2-nacol--${modifier}`}>
      <p className="dashv2-nslabel">{label}</p>
      <p className="dashv2-nsvalue" title={value}>
        {value || '—'}
      </p>
    </div>
  );

  return (
    <div
      className="dashv2-nscard"
      {...cardActivation(
        onSelect && shiftId != null ? () => onSelect(shiftId) : undefined,
      )}
    >
      {/* Keyed by shift, not job: a multi-shift job now yields several rows, so
          a job-based id would be duplicated across them. */}
      <div className="dashv2-narow" id={`dashv2-approval-${shift.shift_id}`}>
        {col(
          'Date & time',
          formatShiftDateTime(shift.start_at, shift.end_at, tz),
          'date',
        )}
        {col('Client', shift.client_name || '', 'client')}
        {col('Skill', shift.skill_name || '', 'skill')}
        {col('Location', shift.location_code || '', 'loc')}
        <div className="dashv2-nscol dashv2-nacol--fraction">
          <p className="dashv2-nslabel">Hired</p>
          <p className="dashv2-nafraction-val state-hired">
            {shift.hired_count}/{shift.quantity}
          </p>
        </div>
        <button
          type="button"
          className="dashv2-nsexpand"
          id={`dashv2-approval-chevron-${shift.shift_id}`}
          aria-label="Open shift detail"
          disabled={!onSelect}
          onClick={(e) => {
            if (shiftId == null) return;
            // Focus explicitly before the panel mounts: the panel restores
            // focus to whatever was active when it opened, and Safari does not
            // focus a button on click. Without this, closing the panel would
            // drop a keyboard user at the top of the document.
            e.currentTarget.focus();
            onSelect?.(shiftId);
          }}
        >
          <ChevronRightIcon />
        </button>
      </div>
    </div>
  );
};

export default NeedsApprovalRow;
